import React from "react";
import { Avatar, AvatarGroup, Badge, Box, Tooltip, Typography } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";

const AVATAR_SIZE = 28;

const buildInitials = (firstName, lastName) => {
  const f = (firstName || "").trim();
  const l = (lastName || "").trim();
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  if (f.length >= 2) return f.slice(0, 2).toUpperCase();
  return f ? f[0].toUpperCase() : "?";
};

/**
 * "Created By" avatar filter — me + team creators.
 * Selecting a creator calls `onSelect` with their user id (or current user id for "me").
 */
const CreatedByFilter = ({
  creators = [],
  selectedUserId,
  currentUserId,
  onSelect,
  disabled = false,
}) => {
  const isMeSelected =
    selectedUserId == null ||
    String(selectedUserId) === String(currentUserId);

  const handleMeClick = () => {
    if (disabled || !currentUserId) return;
    onSelect(String(currentUserId), "me");
  };

  const handleCreatorClick = (creator) => {
    if (disabled) return;
    onSelect(String(creator.userId), creator.userId);
  };

  return (
    <Box className="created-by-filter" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography
        sx={{
          fontSize: "0.82rem",
          fontWeight: 500,
          color: "#64748b",
          whiteSpace: "nowrap",
        }}
      >
        Created By:
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Tooltip title="My tasks" arrow>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  bgcolor: "#2563eb",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1.5px solid #fff",
                }}
              >
                <StarIcon sx={{ fontSize: 9, color: "#fff" }} />
              </Box>
            }
          >
            <Avatar
              onClick={handleMeClick}
              sx={{
                width: AVATAR_SIZE,
                height: AVATAR_SIZE,
                fontSize: "0.72rem",
                fontWeight: 600,
                bgcolor: "#e8eaed",
                color: "#475569",
                cursor: disabled ? "default" : "pointer",
                border: isMeSelected ? "2px solid #2563eb" : "2px solid transparent",
                "&:hover": disabled ? {} : { bgcolor: "#dfe3e8" },
              }}
            >
              me
            </Avatar>
          </Badge>
        </Tooltip>

        <AvatarGroup
          max={3}
          sx={{
            "& .MuiAvatar-root": {
              width: AVATAR_SIZE,
              height: AVATAR_SIZE,
              fontSize: "0.7rem",
              fontWeight: 600,
              border: "2px solid #fff",
              cursor: disabled ? "default" : "pointer",
            },
          }}
        >
          {creators.map((creator) => {
            const initials = creator.initials || buildInitials(creator.firstName, creator.lastName);
            const name = `${creator.firstName || ""} ${creator.lastName || ""}`.trim();
            const isSelected = String(selectedUserId) === String(creator.userId);
            return (
              <Tooltip key={creator.userId} title={name || initials} arrow>
                <Avatar
                  onClick={() => handleCreatorClick(creator)}
                  sx={{
                    bgcolor: creator.color || "#64748b",
                    color: "#fff",
                    outline: isSelected ? "2px solid #2563eb" : "none",
                    outlineOffset: 1,
                  }}
                >
                  {initials}
                </Avatar>
              </Tooltip>
            );
          })}
        </AvatarGroup>
      </Box>
    </Box>
  );
};

export default CreatedByFilter;
